import * as mapboxgl from "mapbox-gl";
import { AnimatableTour, TourAnimation } from "../_components/planning-assistant/classes/tour-animation";
import { PAUtil } from "../_components/planning-assistant/classes/util";
import { ComponentRef } from "@angular/core";
import {
  TourIdxShowTour, TourIdxShowView,
  TourStopoverComponent
} from "../_components/planning-assistant/reuseable-components/tour-stopover/tour-stopover.component";
import { DynamicComponentService } from "../_services/dynamic-component-service";
import * as uuid from "uuid";
import { AnyLayer, AnySourceData, GeoJSONSource } from "mapbox-gl";
import { FeatureProperties, PointGeometry } from "../welcome/dashboard-map/dashboard-map.component";

export abstract class MapDisplayUnit<OnCLickArgs extends any[], OnHoverArgs extends any[]> {

  private _onClickListener: (...args: OnCLickArgs) => void
  private _onHoverListener: (...args: OnHoverArgs) => void
  private _displayedOnMap: mapboxgl.Map

  protected constructor(
  ) {
  }

  set onClickListener(listener: (...args: OnCLickArgs) => void) {
    this._onClickListener = listener
  }

  set onHoverListener(listener: (...args: OnHoverArgs) => void) {
    this._onHoverListener = listener
  }

  get displayedOnMap(): mapboxgl.Map {
    return this._displayedOnMap;
  }

  protected abstract onMapDisplay(): void
  protected abstract onMapRemove(): void


  protected onClick(...args: OnCLickArgs): void {
    if (this._onClickListener) {
      this._onClickListener(...args)
    }
  }

  protected onHover(...args: OnHoverArgs): void {
    if (this._onHoverListener) {
      this._onHoverListener(...args)
    }
  }

  public displayOnMap(map: mapboxgl.Map): void {
    if (this._displayedOnMap && this._displayedOnMap != map) {
      this.removeFromMap()
    }
    this._displayedOnMap = map
    this.onMapDisplay()
  }

  public removeFromMap(): void {
    this.onMapRemove()
    this._displayedOnMap = null
  }
}

export class TourAnimationMapDisplayUnit extends MapDisplayUnit<[],[]> {

  private animationInitialized: boolean = false
  private tourAnimation: TourAnimation

  constructor(
    private tour: AnimatableTour,
  ) {
    super();
  }

  protected override onMapDisplay(): void {
    this.initTourAnimation();
  }

  protected override onMapRemove(): void {
    this.removeTourAnimation();
  }

  private initTourAnimation() {
    const tour_animation = new TourAnimation(this.tour, this.displayedOnMap)
    tour_animation.onClickListener = this.onClick.bind(this)
    tour_animation.initAnimation().then(
      _ => this.animationInitialized = true
    )
    this.tourAnimation = tour_animation
  }

  private removeTourAnimation() {
    if (this.tourAnimation) {
      this.waitForAnimationInitialisation().then(
        _ => {
          this.tourAnimation.deleteCurrentLayers()
          this.tourAnimation = null
          this.animationInitialized = false
        }
      )
    }
  }

  async waitForAnimationInitialisation(): Promise<void> {
    await PAUtil.sleepUntilConditionBreaks(
      () => {
        return this.tourAnimation && !this.animationInitialized
      }
    )
  }
}

export class StoreStopoverMapDisplayUnit extends MapDisplayUnit<[],[]> {

  private markerElement: mapboxgl.Marker;
  stopoverComponent: {
    div_element: HTMLDivElement,
    component_ref: ComponentRef<TourStopoverComponent>
  };

  constructor(
    private store: {id: number, lat: number, lon: number},
    private tours_with_store_operations: TourIdxShowTour[],
    private dynamicComponentService: DynamicComponentService
  ) {
    super();
  }

  protected override onMapDisplay(): void {
    const view: TourIdxShowView = {
      type: 'show',
      tour_idxs: [],
      referenceTours: this.tours_with_store_operations
    }
    this.stopoverComponent = this.dynamicComponentService.injectComponent(TourStopoverComponent, x => {
      x.view = view
      x.storeId = this.store.id
    })
    this.stopoverComponent.component_ref.instance.update()

    this.markerElement = new mapboxgl.Marker({element: this.stopoverComponent.div_element})
    this.markerElement.setLngLat([this.store.lon, this.store.lat])
    this.markerElement.addTo(this.displayedOnMap)
    this.stopoverComponent.div_element.onclick = this.onClick.bind(this)
  }

  protected override onMapRemove(): void {
    this.markerElement.remove()
    this.stopoverComponent.component_ref.destroy()
    this.markerElement = null
    this.stopoverComponent = null
  }

}

export abstract class FeatureMapDisplayUnit extends MapDisplayUnit<[mapboxgl.MapboxGeoJSONFeature[], mapboxgl.LngLat],[]> {

  private mapFeatures: GeoJSON.Feature<PointGeometry, FeatureProperties>[]
  private mapLayers: mapboxgl.AnyLayer[] = []
  private readonly _uniqueSourceID: string

  protected constructor(
    public _featureInstances: MapboxFeatureInstance[],
    public readonly _clusterFeatures?: {
      readonly max_zoom?: number,
      readonly radius?: number,
      readonly bg_color?: string,
      readonly text_color?: string,
    }
  ) {
    super()
    this.onClickListener = this.onFeaturesClick.bind(this)
    this._uniqueSourceID = uuid.v4()
    this.updateFeatures()
  }
  protected abstract onFeaturesClick(features: mapboxgl.MapboxGeoJSONFeature[], lng_lat: mapboxgl.LngLat): void

  private getFeatures(): GeoJSON.Feature<PointGeometry, FeatureProperties>[] {
    return this._featureInstances.map(fi => fi.toMapboxFeature())
  }

  public updateFeatures(): void {
    this.mapFeatures = this.getFeatures()
  }

  protected override onMapDisplay() {
    if (this.displayedOnMap && this.mapFeatures) {
      this.addFeaturesToMap()
      this.addMouseListeners()
    }
  }

  protected override onMapRemove() {
    if (this.displayedOnMap && this.mapFeatures) {
      this.removeMouseListeners()
      this.removeLayers()
      this.removeSource()
    }
  }

  private addFeaturesToMap(): void {
    const source_data: AnySourceData = this._clusterFeatures ? {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: this.mapFeatures
      },
      cluster: true,
      clusterMaxZoom: this._clusterFeatures?.max_zoom || 14,
      clusterRadius: this._clusterFeatures?.radius || 30
    } : {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: this.mapFeatures
      }
    }
    if (!this.displayedOnMap.getSource(this._uniqueSourceID)) {
      console.log(`Map: Adding source with id  ${this._uniqueSourceID}`)
      this.displayedOnMap.addSource(this._uniqueSourceID, source_data)
    }

    for (const layer of this.generateLayers()) {
      if (!this.displayedOnMap.getLayer(layer.id)) {
        this.displayedOnMap.addLayer(layer)
        this.mapLayers.push(layer)
      }
    }
  }

  private generateLayers(): AnyLayer[] {
    const layers = this.generateUnclusteredLayers()
    if (this._clusterFeatures) {
      return layers.concat(this.generateClusteredLayers())
    }
    return layers

  }

  private generateClusteredLayers(): AnyLayer[] {
    return [
      {
        id: this._uniqueSourceID + '_cluster_circles',
        type: "circle",
        source: this._uniqueSourceID,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': this._clusterFeatures.bg_color || 'gray',
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            25,
            100,
            35,
            750,
            45
          ],
          "circle-stroke-width": 1,
          "circle-stroke-color": this._clusterFeatures.text_color || 'gray',
          "circle-opacity": .8
        }
      },
      {
        id: this._uniqueSourceID + '_cluster_count',
        type: 'symbol',
        source: this._uniqueSourceID,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          "text-allow-overlap": false,
        },
        paint: {
          'text-color': this._clusterFeatures.text_color || 'gray',
        }
      }
    ]
  }

  private generateUnclusteredLayers(): AnyLayer[] {
    return [
      {
        id: this._uniqueSourceID + '_unclustered_point',
        type: 'circle',
        source: this._uniqueSourceID,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': this._clusterFeatures.bg_color || 'gray',
          'circle-radius': 16,
          'circle-stroke-width': 2,
          'circle-stroke-color': this._clusterFeatures.text_color || 'gray',
        }
      },
      {
        'id': this._uniqueSourceID + '_unclustered_clabel',
        'type': 'symbol',
        'source': this._uniqueSourceID,
        'filter': ['!', ['has', 'point_count']],
        'layout': {
          'text-field': ['get', 'short_name'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': 10,
          'text-allow-overlap': true,
          'text-offset': [0, 0]
        },
        'paint': {
          'text-color': ['get', 'font_color'],
        }
      }
    ]
  }

  private addMouseListeners(): void {
    if (this._clusterFeatures) this.addClusteredMouseListeners()
    this.addUnclusteredMouseListeners()
  }

  private removeMouseListeners(): void {
    if (this._clusterFeatures) this.removeClusteredMouseListeners()
    this.removeUnclusteredMouseListeners()
  }

  private addClusteredMouseListeners(): void {
    this.displayedOnMap.on('click', this._uniqueSourceID + '_cluster_circles', this.onClusteredPointClick.bind(this))
    this.displayedOnMap.on('mouseenter', this._uniqueSourceID + '_cluster_circles', this.onClusteredPointMouseEnter.bind(this));
    this.displayedOnMap.on('mouseleave', this._uniqueSourceID + '_cluster_circles', this.onClusteredPointMouseLeave.bind(this));
  }

  private addUnclusteredMouseListeners(): void {
    this.displayedOnMap.on('click', this._uniqueSourceID + '_unclustered_point', this.onUnclusteredPointClick.bind(this));
    this.displayedOnMap.on('mouseenter', this._uniqueSourceID + '_unclustered_point', this.onUnclusteredPointMouseEnter.bind(this));
    this.displayedOnMap.on('mouseleave', this._uniqueSourceID + '_unclustered_point', this.onUnclusteredPointMouseLeave.bind(this));
  }

  private onClusteredPointClick(e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined } & mapboxgl.EventData): void {
    const features = this.displayedOnMap.queryRenderedFeatures(e.point, {
      layers: [this._uniqueSourceID + '_cluster_circles']
    });
    const clusterId = features[0].properties.cluster_id;
    const source = this.displayedOnMap.getSource(this._uniqueSourceID) as GeoJSONSource
    source.getClusterExpansionZoom(
      clusterId,
      (err, zoom) => {
        if (err) return;

        this.displayedOnMap.easeTo({
          center: features[0].geometry['coordinates'],
          zoom: zoom
        });
      }
    );
  }

  private onClusteredPointMouseEnter(): void {
    this.displayedOnMap.getCanvas().style.cursor = 'pointer';
  }

  private onClusteredPointMouseLeave(): void {
    this.displayedOnMap.getCanvas().style.cursor = '';
  }

  private onUnclusteredPointClick(e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined } & mapboxgl.EventData): void {
    const f = this.displayedOnMap.queryRenderedFeatures(e.point, {layers: [this._uniqueSourceID + '_unclustered_point']});
    if (f.length) {
      this.onClick(f, e.lngLat);
      return;
    }
  }

  private onUnclusteredPointMouseEnter(): void {
    if (this.displayedOnMap) {
      this.displayedOnMap.getCanvas().style.cursor = 'pointer';
    }
  }

  private onUnclusteredPointMouseLeave(): void {
    if (this.displayedOnMap) {
      this.displayedOnMap.getCanvas().style.cursor = '';
    }
  }

  private removeClusteredMouseListeners(): void {
    this.displayedOnMap.off('click', this._uniqueSourceID + '_cluster_circles', this.onClusteredPointClick)
    this.displayedOnMap.off('mouseenter', this._uniqueSourceID + '_cluster_circles', this.onClusteredPointMouseEnter);
    this.displayedOnMap.off('mouseleave', this._uniqueSourceID + '_cluster_circles', this.onClusteredPointMouseLeave);
  }

  private removeUnclusteredMouseListeners(): void {
    this.displayedOnMap.off('click', this._uniqueSourceID + '_unclustered_point', this.onUnclusteredPointClick)
    this.displayedOnMap.off('mouseenter', this._uniqueSourceID + '_unclustered_point', this.onUnclusteredPointMouseEnter);
    this.displayedOnMap.off('mouseleave', this._uniqueSourceID + '_unclustered_point', this.onUnclusteredPointMouseLeave);
  }

  private removeLayers() {
    for (const layer of this.mapLayers) {
      this.displayedOnMap.removeLayer(layer.id)
    }
    this.mapLayers = []
  }

  private removeSource() {
    try {
      console.log(`Map: Try removing source with id  ${this._uniqueSourceID}`)
      this.displayedOnMap.removeSource(this._uniqueSourceID)
    }
    catch (err) {
      console.error(`Map: Couldn't remove source with id ${this._uniqueSourceID}: ${err}`);
    }
  }
}

export abstract class MapboxFeatureInstance {
  abstract toMapboxFeature(): GeoJSON.Feature<PointGeometry, FeatureProperties>
  protected abstract onClick(...args: any[]): void
}

export class StoreMapboxFeatureInstance extends MapboxFeatureInstance {

  private _onClickListener: () => void
  private ticketCount: number = 0

  constructor(
    public readonly store: {
      id: number,
      lat: number,
      lng: number,
      short_name: string,
      color: string,
    },
    private tickets: {
      id: number
    }[],
    private config?: {
      bg_color?: string
      font_color?: string
    }
  ) {
    super()
    this.updateTicketCount()
  }

  set onClickListener(listener: () => void) {
    this._onClickListener = listener
  }

  public override toMapboxFeature(): GeoJSON.Feature<PointGeometry, FeatureProperties> {
    return {
      type: "Feature",
      properties: {
        id: this.store.id,
        short_name: this.store.short_name,
        bg_color: this.config?.bg_color || 'rgba(172,187,209,0.6)',
        stroke_color: this.store.color,
        font_color: this.config?.font_color || 'rgb(0,0,0)',
        feature_instance: this,
        ticket_count: this.ticketCount
      },
      geometry: {
        type: "Point",
        coordinates: [this.store.lng, this.store.lat]
      }
    }
  }

  public addTicket(ticket: {id: number}): void {
    this.tickets.push(ticket)
    this.updateTicketCount()
  }

  public updateTicketCount(): void {
    this.ticketCount = this.tickets.length
  }

  public override onClick(): void {
    if (this._onClickListener) {
      this._onClickListener()
    } else {
      console.log('no on click listener set')
    }
  }
}

export class StoreFeatureMapDisplayUnit extends FeatureMapDisplayUnit {

  constructor(
    public override _featureInstances: StoreMapboxFeatureInstance[],
    public override readonly _clusterFeatures?: {
      readonly max_zoom?: number,
      readonly radius?: number,
      readonly bg_color?: string,
      readonly text_color?: string,
    }) {
    super(
      _featureInstances,
      _clusterFeatures
    );
  }

  protected override onFeaturesClick(features: mapboxgl.MapboxGeoJSONFeature[], lng_lat: mapboxgl.LngLat): void {
    if (features.length) {
      const feature = this._featureInstances.find(fi => fi.store.id === features[0].properties.id)
      console.log('clicked feature ' + feature.store.id)
      if (feature) feature.onClick()
    }
  }

}

export class TechnicianMapDisplayUnit extends MapDisplayUnit<[],[]> {
  private marker: mapboxgl.Marker;

  constructor(
    private technician: {
      id: number,
      firstname: string,
      lastname: string,
      lat: number,
      lon: number
    },
    private bg_color: string,
    private text_color: string,
  ) {
    super();
  }

  protected override onMapDisplay(): void {
    const text = `${this.technician.firstname} ${this.technician.lastname} Tour Start`
    const popup = new mapboxgl.Popup().setHTML(text)
    const div_element = this.generateDivElement()
    div_element.onclick = this.onClick.bind(this)
    this.marker = new mapboxgl.Marker(div_element)
    this.marker.setPopup(popup)
    this.marker.setLngLat({lon: this.technician.lon, lat: this.technician.lat})
    this.marker.addTo(this.displayedOnMap)
  }

  protected override onMapRemove(): void {
    this.marker.remove()
  }

  generateDivElement(): HTMLDivElement {
    const el = document.createElement('div')
    el.style.backgroundColor = this.bg_color
    el.style.width = '20px'
    el.style.height = '20px'
    el.style.backgroundSize = '100%'
    el.style.border = '1px solid black'
    el.style.fontSize = `.65rem`
    el.style.display = 'inline-flex'
    el.style.borderRadius = '4px'
    const inner_el = document.createElement('b')
    inner_el.style.margin = 'auto'
    inner_el.style.color = this.text_color
    inner_el.style.lineHeight = '.7rem'
    inner_el.innerHTML = `${this.technician.firstname[0]}${this.technician.lastname[0]}`

    el.appendChild(inner_el)
    return el
  }

}
